import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Legend } from 'recharts';
import { Download, Share2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const DOMAIN_LABELS = {
  en: [
    'Physical Health',
    'Strength',
    'Energy',
    'Space',
    'Work',
    'Money',
    'Love',
    'Friends',
    'Emotional',
    'Discipline',
    'Learning',
    'Meaning',
  ],
  tr: [
    'Fiziksel Sağlık',
    'Güç',
    'Enerji',
    'Alan',
    'İş',
    'Para',
    'Aşk',
    'Arkadaşlar',
    'Duygusal',
    'Disiplin',
    'Öğrenme',
    'Anlam',
  ],
};

// Map label index to domain key for navigation
const DOMAIN_KEYS = [
  'physical_health',
  'strength_fitness',
  'energy',
  'environment_order',
  'work_mission',
  'money_financial',
  'love_relationship',
  'friends_community',
  'emotional_stability',
  'discipline_consistency',
  'learning_growth',
  'meaning_purpose',
];

export const SunMap = ({ scores, previousScores = null, historyScores = [], className = '', hideTitle = false, hideActions = false, solarAlignmentScore = null, onExportPDF, onShare, onUpdate, exporting, sharing, language: langProp }) => {
  const chartRef = useRef(null);
  const { t, language: langCtx } = useLanguage();
  const language = langProp || langCtx;
  const navigate = useNavigate();

  const labels = DOMAIN_LABELS[language] || DOMAIN_LABELS.en;

  // Build data with current, previous, ideal, and historical "rays"
  const data = [
    { domain: labels[0], domainKey: DOMAIN_KEYS[0], current: scores.physical_health, ideal: 10 },
    { domain: labels[1], domainKey: DOMAIN_KEYS[1], current: scores.strength_fitness, ideal: 10 },
    { domain: labels[2], domainKey: DOMAIN_KEYS[2], current: scores.energy, ideal: 10 },
    { domain: labels[3], domainKey: DOMAIN_KEYS[3], current: scores.environment_order, ideal: 10 },
    { domain: labels[4], domainKey: DOMAIN_KEYS[4], current: scores.work_mission, ideal: 10 },
    { domain: labels[5], domainKey: DOMAIN_KEYS[5], current: scores.money_financial, ideal: 10 },
    { domain: labels[6], domainKey: DOMAIN_KEYS[6], current: scores.love_relationship, ideal: 10 },
    { domain: labels[7], domainKey: DOMAIN_KEYS[7], current: scores.friends_community, ideal: 10 },
    { domain: labels[8], domainKey: DOMAIN_KEYS[8], current: scores.emotional_stability, ideal: 10 },
    { domain: labels[9], domainKey: DOMAIN_KEYS[9], current: scores.discipline_consistency, ideal: 10 },
    { domain: labels[10], domainKey: DOMAIN_KEYS[10], current: scores.learning_growth, ideal: 10 },
    { domain: labels[11], domainKey: DOMAIN_KEYS[11], current: scores.meaning_purpose, ideal: 10 },
  ];

  // Custom tick component for clickable labels
  const CustomTick = ({ payload, x, y, textAnchor, index }) => {
    const domainKey = DOMAIN_KEYS[index];
    return (
      <g 
        transform={`translate(${x},${y})`} 
        onClick={() => navigate(`/domain/${domainKey}`)}
        style={{ cursor: 'pointer' }}
      >
        <text
          fill="#D4AF37"
          fontSize={10}
          fontFamily="Outfit"
          textAnchor={textAnchor}
          className="hover:fill-white transition-colors"
        >
          {payload.value}
        </text>
      </g>
    );
  };

  // Add previous scores if available
  if (previousScores) {
    data[0].previous = previousScores.physical_health;
    data[1].previous = previousScores.strength_fitness;
    data[2].previous = previousScores.energy;
    data[3].previous = previousScores.environment_order;
    data[4].previous = previousScores.work_mission;
    data[5].previous = previousScores.money_financial;
    data[6].previous = previousScores.love_relationship;
    data[7].previous = previousScores.friends_community;
    data[8].previous = previousScores.emotional_stability;
    data[9].previous = previousScores.discipline_consistency;
    data[10].previous = previousScores.learning_growth;
    data[11].previous = previousScores.meaning_purpose;
  }

  // Add historical rays (older assessments) - up to 5 older ones
  const olderHistory = historyScores.slice(0, 5); // Take up to 5 oldest
  olderHistory.forEach((historyItem, historyIndex) => {
    const rayKey = `ray${historyIndex}`;
    if (historyItem.scores) {
      data[0][rayKey] = historyItem.scores.physical_health;
      data[1][rayKey] = historyItem.scores.strength_fitness;
      data[2][rayKey] = historyItem.scores.energy;
      data[3][rayKey] = historyItem.scores.environment_order;
      data[4][rayKey] = historyItem.scores.work_mission;
      data[5][rayKey] = historyItem.scores.money_financial;
      data[6][rayKey] = historyItem.scores.love_relationship;
      data[7][rayKey] = historyItem.scores.friends_community;
      data[8][rayKey] = historyItem.scores.emotional_stability;
      data[9][rayKey] = historyItem.scores.discipline_consistency;
      data[10][rayKey] = historyItem.scores.learning_growth;
      data[11][rayKey] = historyItem.scores.meaning_purpose;
    }
  });

  // Calculate opacity for historical rays (older = more faded)
  const getRayOpacity = (index, total) => {
    // Most recent ray is 0.3, oldest is 0.08
    const baseOpacity = 0.3;
    const minOpacity = 0.08;
    return baseOpacity - ((baseOpacity - minOpacity) * (index / Math.max(total - 1, 1)));
  };

  const exportAsImage = async () => {
    try {
      const chartContainer = chartRef.current;
      if (!chartContainer) return;

      // Use html2canvas to capture the chart
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartContainer, {
        backgroundColor: '#030305',
        scale: 2,
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sun-map-${new Date().toISOString().split('T')[0]}.png`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(t('sunMap.downloadSuccess'));
      });
    } catch (error) {
      toast.error(t('sunMap.downloadFailed'));
      console.error('Export error:', error);
    }
  };

  const shareMap = async () => {
    try {
      toast.loading(language === 'tr' ? 'Paylaşım hazırlanıyor...' : 'Preparing share...', { id: 'share-loading' });

      let imageData = null;
      let canvas = null;

      // Try to generate the image (may fail on some mobile browsers)
      try {
        const chartContainer = chartRef.current;
        if (chartContainer) {
          const html2canvas = (await import('html2canvas')).default;
          canvas = await html2canvas(chartContainer, {
            backgroundColor: '#030305',
            scale: 2,
            useCORS: true,
            allowTaint: true,
          });
          imageData = canvas.toDataURL('image/png');
        }
      } catch (imageError) {
        console.log('Image capture failed, sharing without image:', imageError);
      }

      // Create a share link via API (with or without image)
      const response = await api.post('/assessment/share', { 
        image_data: imageData 
      });
      const shareId = response.data.share_id;
      const shareUrl = `${window.location.origin}/shared/${shareId}`;
      
      toast.dismiss('share-loading');

      const shareText = language === 'tr' 
        ? 'Benim Güneş Haritam - IN İç Navigasyon' 
        : 'My Sun Map - IN Inner Navigation';

      // Try to share with image file (Web Share API Level 2)
      if (canvas && navigator.canShare) {
        try {
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          const file = new File([blob], 'my-sun-map.png', { type: 'image/png' });
          
          const shareData = { files: [file] };

          if (navigator.canShare(shareData)) {
            await navigator.share({
              title: shareText,
              text: `${language === 'tr' ? 'Kendi haritanı oluştur:' : 'Create your own:'} ${shareUrl}`,
              files: [file],
            });
            toast.success(t('sunMap.shareSuccess'));
            return;
          }
        } catch (fileShareError) {
          console.log('File sharing not supported, falling back:', fileShareError);
        }
      }

      // Fallback: share URL only (works on all devices)
      if (navigator.share) {
        try {
          await navigator.share({
            title: shareText,
            text: `${shareText}\n\n${language === 'tr' ? 'Kendi haritanı oluştur:' : 'Create your own:'}`,
            url: shareUrl,
          });
          toast.success(t('sunMap.shareSuccess'));
          return;
        } catch (shareError) {
          // If user cancels, don't show error
          if (shareError.name === 'AbortError') {
            return;
          }
          console.log('Native share failed, copying to clipboard:', shareError);
        }
      }
      
      // Final fallback: copy link to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(language === 'tr' ? 'Bağlantı kopyalandı!' : 'Link copied to clipboard!');
      } catch (clipboardError) {
        // Manual copy fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success(language === 'tr' ? 'Bağlantı kopyalandı!' : 'Link copied to clipboard!');
      }
    } catch (error) {
      toast.dismiss('share-loading');
      console.error('Share error:', error);
      toast.error(language === 'tr' ? 'Paylaşım başarısız. Lütfen tekrar deneyin.' : 'Share failed. Please try again.');
    }
  };

  return (
    <div className={`${className}`} data-testid="sun-map-container">
      {(!hideTitle || !hideActions) && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          {!hideTitle && (
            <h2 className="font-serif font-light text-2xl sm:text-3xl text-[#F8F5F2]" data-testid="sun-map-title">
              {t('sunMap.title')}
            </h2>
          )}
          {!hideActions && (
            <div className={`flex gap-2 ${hideTitle ? 'w-full justify-center' : ''}`}>
              <button
                onClick={exportAsImage}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all duration-300 font-sans text-xs sm:text-sm"
                data-testid="export-sun-map-button"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t('sunMap.export')}</span>
              </button>
              <button
                onClick={shareMap}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all duration-300 font-sans text-xs sm:text-sm"
                data-testid="share-sun-map-button"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t('sunMap.share')}</span>
              </button>
            </div>
          )}
        </div>
      )}

      <div ref={chartRef} className="relative overflow-visible">
        {/* RADIANT ORB — multi-layered cosmic sun with fractal rays */}
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
          aria-hidden="true"
          data-testid="sun-map-aura"
        >
          <svg
            viewBox="0 0 500 500"
            className="w-full h-full max-w-[500px] max-h-[500px]"
            style={{ overflow: 'visible' }}
          >
            <defs>
              {/* Outer diffuse aura — wide warm halo */}
              <radialGradient id="orbHaloGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFD27D" stopOpacity="0.45" />
                <stop offset="30%" stopColor="#D4AF37" stopOpacity="0.22" />
                <stop offset="65%" stopColor="#FF8C42" stopOpacity="0.10" />
                <stop offset="100%" stopColor="#030305" stopOpacity="0" />
              </radialGradient>
              {/* Mid orb — sharper gold body */}
              <radialGradient id="orbMidGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFE8B0" stopOpacity="0.75" />
                <stop offset="55%" stopColor="#D4AF37" stopOpacity="0.30" />
                <stop offset="100%" stopColor="#B8860B" stopOpacity="0" />
              </radialGradient>
              {/* Inner core — bright nucleus (warm, doesn't wash out the score) */}
              <radialGradient id="orbCoreGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFD27D" stopOpacity="0.42" />
                <stop offset="40%" stopColor="#D4AF37" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
              </radialGradient>
              {/* Primary ray gradient (warm tip) */}
              <radialGradient id="sunRayGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
                <stop offset="40%" stopColor="#FFD27D" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#FF8C42" stopOpacity="0.35" />
              </radialGradient>
              {/* Secondary ray gradient (cooler gold, counter-layer) */}
              <radialGradient id="sunRayGradientAlt" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFE8B0" stopOpacity="0" />
                <stop offset="50%" stopColor="#FFD27D" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.28" />
              </radialGradient>
            </defs>

            {/* Layer 1 — outer halo (slowest breath) */}
            <circle
              className="sunmap-aura-circle"
              cx="250"
              cy="250"
              r="200"
              fill="url(#orbHaloGradient)"
            />

            {/* Layer 2 — mid orb body (faster, offset breath) */}
            <circle
              className="sunmap-orb-mid"
              cx="250"
              cy="250"
              r="130"
              fill="url(#orbMidGradient)"
            />

            {/* Layer 3 — warm inner core nucleus (fast pulse, doesn't wash out score) */}
            <circle
              className="sunmap-orb-core"
              cx="250"
              cy="250"
              r="42"
              fill="url(#orbCoreGradient)"
            />

            {/* Layer 4 — PRIMARY ray set (12 long rays, fractal varied lengths, clockwise) */}
            <g className="sunmap-rays-group">
              {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i * 30 * Math.PI) / 180;
                // Fractal-like varying lengths (Fibonacci-flavored sequence)
                const lengths = [230, 195, 245, 210, 225, 200, 240, 205, 235, 215, 250, 190];
                const len = lengths[i];
                const x2 = 250 + Math.cos(angle) * len;
                const y2 = 250 + Math.sin(angle) * len;
                const isMajor = i % 2 === 0;
                // Branching micro-rays at the tip of every other primary ray
                const branchAngle1 = angle - 0.18;
                const branchAngle2 = angle + 0.18;
                const branchLen = len * 0.35;
                const bx1 = 250 + Math.cos(angle) * (len * 0.65) + Math.cos(branchAngle1) * branchLen * 0.5;
                const by1 = 250 + Math.sin(angle) * (len * 0.65) + Math.sin(branchAngle1) * branchLen * 0.5;
                const bx2 = 250 + Math.cos(angle) * (len * 0.65) + Math.cos(branchAngle2) * branchLen * 0.5;
                const by2 = 250 + Math.sin(angle) * (len * 0.65) + Math.sin(branchAngle2) * branchLen * 0.5;
                const branchOriginX = 250 + Math.cos(angle) * (len * 0.65);
                const branchOriginY = 250 + Math.sin(angle) * (len * 0.65);
                return (
                  <g key={i}>
                    <line
                      x1="250"
                      y1="250"
                      x2={x2}
                      y2={y2}
                      stroke="url(#sunRayGradient)"
                      strokeWidth={isMajor ? "2.0" : "0.9"}
                      strokeLinecap="round"
                      style={{
                        animation: `${isMajor ? 'sunmap-ray-flicker-strong' : 'sunmap-ray-flicker-soft'} ${4 + (i % 3)}s ease-in-out ${i * 0.35}s infinite`,
                      }}
                    />
                    {isMajor && (
                      <>
                        <line
                          x1={branchOriginX}
                          y1={branchOriginY}
                          x2={bx1}
                          y2={by1}
                          stroke="url(#sunRayGradient)"
                          strokeWidth="0.7"
                          strokeLinecap="round"
                          opacity="0.55"
                          style={{ animation: `sunmap-ray-flicker-soft ${3.5 + (i % 2)}s ease-in-out ${i * 0.5}s infinite` }}
                        />
                        <line
                          x1={branchOriginX}
                          y1={branchOriginY}
                          x2={bx2}
                          y2={by2}
                          stroke="url(#sunRayGradient)"
                          strokeWidth="0.7"
                          strokeLinecap="round"
                          opacity="0.55"
                          style={{ animation: `sunmap-ray-flicker-soft ${3.5 + (i % 2)}s ease-in-out ${i * 0.5 + 1}s infinite` }}
                        />
                      </>
                    )}
                  </g>
                );
              })}
            </g>

            {/* Layer 5 — SECONDARY ray set (24 thinner, counter-rotating) */}
            <g className="sunmap-rays-counter">
              {Array.from({ length: 24 }).map((_, i) => {
                const angle = ((i * 15) + 7.5) * Math.PI / 180;
                const len = 150 + (i % 4) * 25; // 150,175,200,225 cycle — fractal-like variance
                const x2 = 250 + Math.cos(angle) * len;
                const y2 = 250 + Math.sin(angle) * len;
                return (
                  <line
                    key={i}
                    x1="250"
                    y1="250"
                    x2={x2}
                    y2={y2}
                    stroke="url(#sunRayGradientAlt)"
                    strokeWidth="0.5"
                    strokeLinecap="round"
                    style={{
                      animation: `sunmap-ray-flicker-soft ${6 + (i % 4)}s ease-in-out ${i * 0.22}s infinite`,
                    }}
                  />
                );
              })}
            </g>

            {/* Layer 6 — orbiting sparkle particles */}
            <g className="sunmap-sparkles">
              {[
                { r: 170, start: 0,  speed: 18 },
                { r: 195, start: 90, speed: 24, reverse: true },
                { r: 215, start: 180, speed: 30 },
                { r: 155, start: 270, speed: 22, reverse: true },
                { r: 185, start: 45,  speed: 26 },
                { r: 205, start: 135, speed: 32, reverse: true },
                { r: 175, start: 225, speed: 20 },
                { r: 200, start: 315, speed: 28, reverse: true },
              ].map((p, i) => (
                <g
                  key={i}
                  className="sunmap-sparkle-orbit-wrap"
                  style={{
                    animation: `${p.reverse ? 'sunmap-sparkle-orbit-rev' : 'sunmap-sparkle-orbit'} ${p.speed}s linear infinite`,
                    animationDelay: `${-(p.start / 360) * p.speed}s`,
                  }}
                >
                  {/* Invisible anchors at center + opposite side to force bbox centering on (250,250) */}
                  <circle cx={250 - p.r} cy="250" r="0.001" fill="none" />
                  <circle cx={250 + p.r} cy="250" r="0.001" fill="none" />
                  <circle cx="250" cy={250 - p.r} r="0.001" fill="none" />
                  <circle cx="250" cy={250 + p.r} r="0.001" fill="none" />
                  <circle
                    cx={250 + p.r}
                    cy="250"
                    r="1.6"
                    fill="#FFE8B0"
                    style={{
                      animation: `sunmap-sparkle-twinkle ${2 + (i % 3)}s ease-in-out ${i * 0.3}s infinite`,
                    }}
                  />
                </g>
              ))}
            </g>
          </svg>
        </div>

        <ResponsiveContainer width="100%" height={500} className="!overflow-visible">
          <RadarChart data={data} cx="50%" cy="50%" outerRadius="65%">
            {/* Gradient definitions for heat shading */}
            <defs>
              <radialGradient id="currentHeatGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.9} />
                <stop offset="40%" stopColor="#D4AF37" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#B8860B" stopOpacity={0.3} />
              </radialGradient>
              <linearGradient id="sunsetOrangeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FF8C42" />
                <stop offset="100%" stopColor="#FF6B35" />
              </linearGradient>
            </defs>
            
            <PolarGrid stroke="rgba(255, 140, 66, 0.15)" />
            <PolarAngleAxis
              dataKey="domain"
              tick={<CustomTick />}
              tickLine={false}
            />
            
            {/* Historical rays (oldest to newest, fading back) */}
            {olderHistory.map((_, index) => {
              const rayIndex = olderHistory.length - 1 - index;
              const opacity = getRayOpacity(rayIndex, olderHistory.length);
              return (
                <Radar
                  key={`ray-${rayIndex}`}
                  name=""
                  dataKey={`ray${rayIndex}`}
                  stroke="#9B59B6"
                  fill="#9B59B6"
                  fillOpacity={opacity * 0.3}
                  strokeOpacity={opacity * 0.5}
                  strokeWidth={1}
                  dot={false}
                  legendType="none"
                />
              );
            })}
            
            {/* Solar Ideal - Sunset Orange (outer ring) */}
            <Radar
              name={t('sunMap.solarIdeal')}
              dataKey="ideal"
              stroke="#FF8C42"
              fill="#FF8C42"
              fillOpacity={0.08}
              strokeWidth={3}
            />
            
            {/* Previous position (last week) - Purple/Violet */}
            {previousScores && (
              <Radar
                name={t('sunMap.previous')}
                dataKey="previous"
                stroke="#8E44AD"
                fill="#8E44AD"
                fillOpacity={0.15}
                strokeWidth={2}
                strokeDasharray="5 5"
              />
            )}
            
            {/* Current position (this week) - Gold with heat effect */}
            <Radar
              name={t('sunMap.currentPattern')}
              dataKey="current"
              stroke="#D4AF37"
              fill="url(#currentHeatGradient)"
              fillOpacity={0.7}
              strokeWidth={2.5}
            />

            {/* Solar Alignment Score - Center of chart (heartbeat pulse) */}
            {solarAlignmentScore !== null && (
              <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" fill="#D4AF37" fontSize="32" fontFamily="Georgia, serif" fontWeight="400" data-testid="solar-score-center" style={{ animation: 'sunmap-heartbeat 4s ease-in-out infinite' }}>
                {solarAlignmentScore}
              </text>
            )}
            {solarAlignmentScore !== null && (
              <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" fill="#A1A1AA" fontSize="9" fontFamily="Outfit, sans-serif" letterSpacing="0.15em">
                SOLAR ALIGNMENT
              </text>
            )}
            
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                fontFamily: 'Outfit',
                fontSize: '12px',
              }}
              iconType="circle"
              formatter={(value) => value || null}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {/* Inline actions (PDF, Share, Update) */}
      {(onExportPDF || onShare || onUpdate) && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {onExportPDF && (
            <button
              onClick={onExportPDF}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all text-xs font-sans disabled:opacity-50"
              data-testid="sunmap-pdf-btn"
            >
              <Download className="w-3.5 h-3.5" />
              PDF
            </button>
          )}
          {onShare && (
            <button
              onClick={onShare}
              disabled={sharing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all text-xs font-sans disabled:opacity-50"
              data-testid="sunmap-share-btn"
            >
              <Share2 className="w-3.5 h-3.5" />
              {language === 'tr' ? 'Paylaş' : 'Share'}
            </button>
          )}
          {onUpdate && (
            <button
              onClick={onUpdate}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all text-xs font-sans"
              data-testid="sunmap-update-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {language === 'tr' ? 'Güncelle' : 'Update'}
            </button>
          )}
        </div>
      )}
      <p className="text-center text-xs text-[#6B7280] mt-2">
        {language === 'tr' ? 'Detaylar için domain isimlerine tıklayın' : 'Click domain names for details'}
      </p>
    </div>
  );
};

export default SunMap;
